import { useEffect, useState } from 'react';
import { useToast } from '@/contexts/ToastContext';
import {
  getPrintSettings,
  savePrintSettings,
  isValidUpiId,
  buildUpiUrl,
  makeQrDataUrl,
  PrintSettings,
  InvoicePrintFormat,
  QrMode,
} from '@/utils/printThermalInvoice';

const FORMAT_OPTIONS: { v: InvoicePrintFormat; title: string; desc: string; icon: string }[] = [
  { v: 'A4', title: 'A4', desc: 'Normal printer · full page', icon: 'ri-file-text-line' },
  { v: 'THERMAL_80', title: 'Thermal 80mm', desc: '3 inch roll · POS printer', icon: 'ri-receipt-line' },
  { v: 'THERMAL_58', title: 'Thermal 58mm', desc: '2 inch roll · small printer', icon: 'ri-receipt-line' },
  { v: 'THERMAL_CUSTOM', title: 'Thermal Custom', desc: 'Apni roll width (mm)', icon: 'ri-ruler-line' },
];

const QR_OPTIONS: { v: QrMode; label: string; icon: string }[] = [
  { v: 'none', label: 'None', icon: 'ri-close-circle-line' },
  { v: 'upi', label: 'UPI ID se QR', icon: 'ri-qr-code-line' },
  { v: 'image', label: 'Apna QR image', icon: 'ri-image-line' },
];

const MAX_QR_IMAGE_KB = 300;

export default function PrintSettingsCard() {
  const toast = useToast();
  const [settings, setSettings] = useState<PrintSettings>(getPrintSettings);
  const [termsText, setTermsText] = useState(settings.terms.join('\n'));
  const [qrPreview, setQrPreview] = useState('');
  const isThermal = settings.format !== 'A4';
  const upiValid = isValidUpiId(settings.upiId);

  const set = <K extends keyof PrintSettings>(key: K, val: PrintSettings[K]) =>
    setSettings((p) => ({ ...p, [key]: val }));

  // Live UPI QR preview (offline — bundled library)
  useEffect(() => {
    if (settings.qrMode !== 'upi' || !upiValid) {
      setQrPreview('');
      return;
    }
    let cancelled = false;
    makeQrDataUrl(buildUpiUrl(settings.upiId, settings.upiName || 'Merchant'), 280).then((url) => {
      if (!cancelled) setQrPreview(url);
    });
    return () => {
      cancelled = true;
    };
  }, [settings.qrMode, settings.upiId, settings.upiName, upiValid]);

  const handleQrImage = (file?: File) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Sirf image file upload karo');
      return;
    }
    if (file.size > MAX_QR_IMAGE_KB * 1024) {
      toast.error(`Image ${MAX_QR_IMAGE_KB}KB se chhoti honi chahiye`);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => set('qrImage', String(reader.result || ''));
    reader.readAsDataURL(file);
  };

  const handleSave = () => {
    if (settings.qrMode === 'upi' && !upiValid) {
      toast.error('Sahi UPI ID daalo (jaise shopname@okaxis)');
      return;
    }
    if (settings.qrMode === 'image' && !settings.qrImage) {
      toast.error('QR image upload karo');
      return;
    }
    const toSave: PrintSettings = {
      ...settings,
      upiId: settings.upiId.trim(),
      upiName: settings.upiName.trim(),
      footerText: settings.footerText.trim(),
      terms: termsText
        .split('\n')
        .map((t) => t.trim())
        .filter(Boolean),
    };
    if (savePrintSettings(toSave)) {
      setSettings(toSave);
      toast.success('Print settings saved for this computer');
    } else {
      toast.error('Save nahi hua — QR image chhoti karke try karo');
    }
  };

  const labelCls = 'block text-xs font-semibold text-[#64748b] mb-1.5 uppercase tracking-wide';
  const fieldCls =
    'w-full px-3 text-sm bg-white border border-[#e2e8f0] rounded-lg focus:outline-none focus:border-[#4f46e5] focus:ring-2 focus:ring-indigo-100 text-[#1e293b]';

  return (
    <div className="bg-white border border-[#e2e8f0] rounded-xl p-6">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <i className="ri-printer-line text-[#4f46e5] text-base" />
          <h3 className="text-sm font-semibold text-[#1e293b]">Print Settings (Sales Invoice)</h3>
        </div>
        <button
          onClick={handleSave}
          className="flex items-center gap-2 h-8 px-4 rounded-lg bg-[#4f46e5] text-white text-xs font-medium hover:bg-indigo-700 cursor-pointer whitespace-nowrap"
        >
          <i className="ri-save-line" /> Save
        </button>
      </div>
      <p className="text-xs text-[#64748b] mb-5">
        Ek baar set karo — phir invoice par Print dabate hi isi format mein print hoga.
      </p>

      {/* Default format */}
      <div className="mb-5">
        <label className={labelCls}>Default Print Format</label>
        <div className="grid grid-cols-4 gap-3">
          {FORMAT_OPTIONS.map((o) => {
            const active = settings.format === o.v;
            return (
              <button
                key={o.v}
                type="button"
                onClick={() => set('format', o.v)}
                className={`flex items-center gap-3 p-3 rounded-lg border-2 text-left transition-colors cursor-pointer ${
                  active ? 'border-[#4f46e5] bg-indigo-50' : 'border-[#e2e8f0] hover:border-[#4f46e5]/40'
                }`}
              >
                <i className={`${o.icon} text-xl ${active ? 'text-[#4f46e5]' : 'text-[#94a3b8]'}`} />
                <div>
                  <p className={`text-sm font-semibold ${active ? 'text-[#4f46e5]' : 'text-[#1e293b]'}`}>{o.title}</p>
                  <p className="text-[11px] text-[#94a3b8]">{o.desc}</p>
                </div>
              </button>
            );
          })}
        </div>
        {settings.format === 'THERMAL_CUSTOM' && (
          <div className="mt-3 flex items-center gap-3 p-3 bg-indigo-50/50 border border-indigo-100 rounded-lg">
            <span className="text-xs font-semibold text-[#64748b] uppercase tracking-wide">Roll Width</span>
            <input
              type="number"
              min={40}
              max={120}
              step={1}
              value={settings.thermalWidthMm}
              onChange={(e) => {
                const v = parseFloat(e.target.value);
                if (!Number.isNaN(v)) set('thermalWidthMm', v);
              }}
              className="w-20 h-8 px-2 text-sm border border-[#e2e8f0] rounded-md text-right outline-none focus:border-[#4f46e5]"
            />
            <span className="text-xs text-[#94a3b8]">mm (40–120) · jaise 72, 76, 104</span>
          </div>
        )}
      </div>

      {/* Preview toggle */}
      <div className="mb-5 flex items-center justify-between p-3 bg-[#f8fafc] border border-[#e2e8f0] rounded-lg">
        <div>
          <p className="text-sm font-medium text-[#1e293b]">Print se pehle preview dikhaye</p>
          <p className="text-xs text-[#94a3b8]">Off karne par Print button dabate hi seedha print hoga (one-click)</p>
        </div>
        <button
          type="button"
          onClick={() => set('showPreview', !settings.showPreview)}
          className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer shrink-0 ${
            settings.showPreview ? 'bg-[#4f46e5]' : 'bg-slate-300'
          }`}
          aria-label="Toggle preview"
        >
          <span
            className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
              settings.showPreview ? 'translate-x-5' : ''
            }`}
          />
        </button>
      </div>

      {/* Auto print on save */}
      <div className="mb-5 flex items-center justify-between p-3 bg-[#f8fafc] border border-[#e2e8f0] rounded-lg">
        <div>
          <p className="text-sm font-medium text-[#1e293b]">Invoice save hote hi auto print</p>
          <p className="text-xs text-[#94a3b8]">New invoice Submit karte hi isi format mein print dialog khulega</p>
        </div>
        <button
          type="button"
          onClick={() => set('autoPrintOnSave', !settings.autoPrintOnSave)}
          className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer shrink-0 ${
            settings.autoPrintOnSave ? 'bg-[#4f46e5]' : 'bg-slate-300'
          }`}
          aria-label="Toggle auto print"
        >
          <span
            className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
              settings.autoPrintOnSave ? 'translate-x-5' : ''
            }`}
          />
        </button>
      </div>

      {/* Payment QR */}
      <div className={`mb-5 p-4 border border-[#e2e8f0] rounded-lg ${isThermal ? '' : 'opacity-60'}`}>
        <div className="flex items-center gap-2 mb-3">
          <i className="ri-qr-code-line text-[#4f46e5]" />
          <p className="text-sm font-semibold text-[#1e293b]">Payment QR (Thermal bill par)</p>
        </div>

        <div className="grid grid-cols-3 gap-2 mb-4">
          {QR_OPTIONS.map((o) => {
            const active = settings.qrMode === o.v;
            return (
              <button
                key={o.v}
                type="button"
                onClick={() => set('qrMode', o.v)}
                className={`flex items-center justify-center gap-2 h-9 rounded-lg border text-xs font-medium transition-colors cursor-pointer ${
                  active
                    ? 'bg-[#4f46e5] border-[#4f46e5] text-white'
                    : 'bg-white border-[#e2e8f0] text-[#64748b] hover:border-[#4f46e5]/40'
                }`}
              >
                <i className={o.icon} /> {o.label}
              </button>
            );
          })}
        </div>

        {settings.qrMode === 'upi' && (
          <div className="grid grid-cols-[1fr_auto] gap-6">
            <div className="space-y-3">
              <div>
                <label className={labelCls}>UPI ID</label>
                <input
                  type="text"
                  value={settings.upiId}
                  placeholder="shopname@okaxis"
                  onChange={(e) => set('upiId', e.target.value)}
                  className={`${fieldCls} h-10 font-mono ${settings.upiId && !upiValid ? 'border-red-400' : ''}`}
                />
                {settings.upiId && !upiValid && (
                  <p className="text-xs text-red-500 mt-1">UPI ID sahi format mein nahi hai</p>
                )}
              </div>
              <div>
                <label className={labelCls}>Payee Name (UPI app mein dikhega)</label>
                <input
                  type="text"
                  value={settings.upiName}
                  placeholder="Shop / Company name"
                  onChange={(e) => set('upiName', e.target.value)}
                  className={`${fieldCls} h-10`}
                />
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.qrWithAmount}
                  onChange={(e) => set('qrWithAmount', e.target.checked)}
                  className="w-4 h-4 accent-[#4f46e5] cursor-pointer"
                />
                <span className="text-xs text-[#1e293b]">
                  Bill ka amount QR mein add karo (customer ko amount type nahi karna padega)
                </span>
              </label>
            </div>
            <div className="flex flex-col items-center gap-2">
              <div className="w-[148px] h-[148px] p-1 bg-white border border-[#e2e8f0] rounded-lg flex items-center justify-center text-center">
                {qrPreview ? (
                  <img src={qrPreview} alt="UPI QR" className="w-full h-full object-contain" />
                ) : (
                  <span className="text-[11px] text-[#94a3b8]">QR</span>
                )}
              </div>
              <p className="text-[11px] text-[#94a3b8]">{upiValid ? 'Test scan karke check karo' : 'UPI ID daalo'}</p>
            </div>
          </div>
        )}

        {settings.qrMode === 'image' && (
          <div className="flex items-start gap-6">
            <div className="flex-1">
              <label className={labelCls}>QR Image upload karo</label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleQrImage(e.target.files?.[0])}
                className="block w-full text-xs text-[#64748b] file:mr-3 file:h-9 file:px-4 file:rounded-lg file:border-0 file:bg-indigo-50 file:text-[#4f46e5] file:font-medium cursor-pointer"
              />
              <p className="text-xs text-[#94a3b8] mt-1">
                PhonePe / Paytm / GPay ka QR screenshot · max {MAX_QR_IMAGE_KB}KB · is QR mein amount nahi hota
              </p>
              {settings.qrImage && (
                <button
                  type="button"
                  onClick={() => set('qrImage', '')}
                  className="mt-2 text-xs text-red-500 hover:underline cursor-pointer"
                >
                  Remove image
                </button>
              )}
            </div>
            <div className="w-[148px] h-[148px] p-1 bg-white border border-[#e2e8f0] rounded-lg flex items-center justify-center">
              {settings.qrImage ? (
                <img src={settings.qrImage} alt="QR" className="max-w-full max-h-full object-contain" />
              ) : (
                <span className="text-[11px] text-[#94a3b8]">No image</span>
              )}
            </div>
          </div>
        )}

        {settings.qrMode !== 'none' && (
          <div className="mt-4 max-w-xs">
            <label className={labelCls}>QR ke neeche label</label>
            <input
              type="text"
              value={settings.qrLabel}
              placeholder="SCAN TO PAY"
              onChange={(e) => set('qrLabel', e.target.value)}
              className={`${fieldCls} h-10`}
            />
          </div>
        )}
      </div>

      {/* Thermal footer + T&C */}
      <div className={`grid grid-cols-2 gap-6 ${isThermal ? '' : 'opacity-60'}`}>
        <div>
          <label className={labelCls}>Thermal Footer Text</label>
          <input
            type="text"
            value={settings.footerText}
            placeholder="Thank you! Visit Again"
            onChange={(e) => set('footerText', e.target.value)}
            className={`${fieldCls} h-10`}
          />
          <p className="text-xs text-[#94a3b8] mt-1">Bill ke sabse neeche dikhega</p>
        </div>
        <div>
          <label className={labelCls}>Terms &amp; Conditions</label>
          <textarea
            rows={4}
            value={termsText}
            placeholder={'Goods once sold will not be taken back.\nSubject to Delhi jurisdiction.'}
            onChange={(e) => setTermsText(e.target.value)}
            className={`${fieldCls} py-2 resize-none`}
          />
          <p className="text-xs text-[#94a3b8] mt-1">Har line alag point banegi · khali chhodo toh T&amp;C nahi aayega</p>
        </div>
      </div>
      {!isThermal && (
        <p className="text-xs text-[#94a3b8] mt-2">QR, Footer aur T&amp;C abhi sirf Thermal bill par lagte hain.</p>
      )}

      <div className="mt-5 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
        <i className="ri-information-line text-amber-600 text-sm mt-0.5" />
        <p className="text-xs text-amber-700">
          Ye settings sirf <b>is computer/browser</b> par save hoti hain — billing counter par Thermal aur office mein A4
          alag-alag set kar sakte ho.
        </p>
      </div>
    </div>
  );
}
